import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShoppingCart, Check, Package } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import Footer from '../components/sections/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AssignmentSelect = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const [assignment, setAssignment] = useState(null);
  const [products, setProducts] = useState([]);
  const [packages, setPackages] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(false);
  const t = (en, zh) => isZh ? (zh || en) : en;

  useEffect(() => {
    const load = async () => {
      try {
        const [aRes, pRes, pkgRes] = await Promise.all([
          axios.get(`${API}/shop/assignments/${id}`),
          axios.get(`${API}/shop/products`),
          axios.get(`${API}/shop/packages`),
        ]);
        setAssignment(aRes.data);
        setProducts(pRes.data.products || []);
        setPackages(pkgRes.data.packages || []);
      } catch { toast.error('Assignment not found'); }
    };
    load();
  }, [id]);

  if (!assignment) return <div className="min-h-screen flex items-center justify-center pt-20"><div className="w-10 h-10 border-4 border-red-600 border-t-transparent rounded-full animate-spin" /></div>;

  const pkg = packages.find(p => p.id === assignment.package_id);
  const assignedProducts = (assignment.product_ids || []).map(pid => products.find(p => p.id === pid)).filter(Boolean);
  const pkgProducts = pkg ? (pkg.product_ids || []).map(pid => products.find(p => p.id === pid)).filter(Boolean) : [];
  const allItems = [...pkgProducts, ...assignedProducts];

  const toggleSpec = (prodId, spec) => {
    setSelected(prev => ({ ...prev, [prodId]: spec }));
  };

  const submitOrder = async () => {
    setLoading(true);
    try {
      const items = allItems.map(p => ({
        product_id: p.id, name_en: p.name_en, name_zh: p.name_zh,
        spec_name: selected[p.id]?.name_en || '',
        spec_image: selected[p.id]?.image || p.main_image,
        price: p.package_price || p.discount_price || p.original_price || 0,
        quantity: 1
      }));
      const loggedAccount = localStorage.getItem('admin_email') || '';
      const r = await axios.post(`${API}/shop/orders`, {
        items, phone: assignment.sign_phone, email: assignment.client_account,
        address: assignment.sign_address,
        notes: `Assignment: ${id}, Consultant: ${assignment.consultant}`,
        account: loggedAccount
      });
      // Mark assignment as completed
      try { await axios.put(`${API}/shop/assignments/${id}/complete`); } catch {}
      toast.success(`Order ${r.data.order_no} created!`);
      navigate('/user-profile');
    } catch { toast.error('Failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col pt-20">
      <main className="flex-grow py-10">
        <div className="max-w-4xl mx-auto px-6">
          {/* Assignment Info */}
          <Card className="shadow-lg mb-8 border-red-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Package className="w-5 h-5 text-red-600" />
                <h1 className="text-2xl font-bold text-gray-900">{isZh ? '专属选材方案' : 'Your Material Selection'}</h1>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <p><span className="text-gray-500">{isZh ? '客户' : 'Client'}:</span> <span className="font-medium">{assignment.client_name}</span></p>
                <p><span className="text-gray-500">{isZh ? '顾问' : 'Consultant'}:</span> <span className="font-medium">{assignment.consultant}</span></p>
                <p><span className="text-gray-500">{isZh ? '地址' : 'Address'}:</span> <span className="font-medium">{assignment.sign_address}</span></p>
                <p><span className="text-gray-500">{isZh ? '日期' : 'Date'}:</span> <span className="font-medium">{assignment.sign_date}</span></p>
              </div>
            </CardContent>
          </Card>

          {/* Package Info */}
          {pkg && (
            <div className="mb-8">
              <h2 className="text-xl font-bold mb-4">{isZh ? '套餐方案' : 'Package'}: {t(pkg.name_en, pkg.name_zh)}</h2>
              {pkg.image && <img src={pkg.image} alt="" className="w-full rounded-xl shadow mb-4" />}
            </div>
          )}

          {/* Products to Select */}
          <h2 className="text-xl font-bold mb-4">{isZh ? '请选择规格' : 'Select Specifications'}</h2>
          <div className="space-y-4 mb-8">
            {allItems.map(p => (
              <Card key={p.id} className="shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-4 mb-3">
                    <img src={selected[p.id]?.image || p.main_image || ''} alt="" className="w-20 h-20 rounded-lg object-cover" />
                    <div className="flex-1">
                      <h3 className="font-bold">{t(p.name_en, p.name_zh)}</h3>
                      <p className="text-red-600 font-bold">${p.package_price || p.discount_price || p.original_price || 0}</p>
                    </div>
                  </div>
                  {p.specs?.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {p.specs.map((spec, si) => (
                        <button key={si} onClick={() => toggleSpec(p.id, spec)}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-all ${selected[p.id]?.name_en === spec.name_en ? 'border-red-600 bg-red-50 text-red-700' : 'border-gray-200 hover:border-gray-400'}`}>
                          {spec.image && <img src={spec.image} alt="" className="w-8 h-8 rounded object-cover" />}
                          <span>{t(spec.name_en, spec.name_zh)}</span>
                          {selected[p.id]?.name_en === spec.name_en && <Check className="w-4 h-4 text-red-600" />}
                        </button>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          <Button onClick={submitOrder} disabled={loading} className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-4 text-lg">
            <ShoppingCart className="w-5 h-5 mr-2" />{loading ? '...' : (isZh ? '确认下单' : 'Confirm Order')}
          </Button>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AssignmentSelect;
